
import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import {
  downloadGarminWorkouts,
  showGarminWorkouts,
  showGarminWorkoutsFit,
  showGarminActivity
} from '../../services/workoutService';
import { getAll } from '../../services/activityService';

const TestWidget = () => {
  const [activities, setActivities] = useState([]);
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedActivities, setExpandedActivities] = useState({});
  const [selectedGarminActivity, setSelectedGarminActivity] =
    useState(null);

  useEffect(() => {
    loadActivities();
  }, []);

  async function loadActivities() {
    setLoading(true);
    setError('');

    try {
      const data = await getAll();

      // Support an array or common API response wrappers.
      const records = Array.isArray(data)
        ? data
        : data?.activities ?? data?.data ?? [];

      setActivities(records);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Unable to load activities.');
    } finally {
      setLoading(false);
    }
  }

  const handleDownloadGarminData = async () => {
    try {
      const data = await downloadGarminWorkouts();
      console.log('Downloaded Garmin data:', data);
    } catch (err) {
      console.error('Error downloading Garmin data:', err);
      setError('Unable to download Garmin data.');
    }
  };

  const handleShowGarminData = async () => {
    try {
      const data = await showGarminWorkouts();
      setTestData(data);
    } catch (err) {
      console.error('Error showing Garmin data:', err);
      setError('Unable to show Garmin workout files.');
    }
  };

    const handleShowGarminDataFIT = async () => {
    try {
      const data = await showGarminWorkoutsFit();
      setTestData(data);
    } catch (err) {
      console.error('Error showing Garmin data:', err);
      setError('Unable to show Garmin workout files.');
    }
  };

  const handleShowGarminActivity = async (fileName) => {
    try {
      const data = await showGarminActivity(fileName);
      setSelectedGarminActivity(data);
    } catch (err) {
      console.error('Error loading Garmin activity:', err);
      setError('Unable to load Garmin activity details.');
    }
  };

  const toggleLaps = (id) => {
    setExpandedActivities((previous) => ({
      ...previous,
      [id]: !previous[id]
    }));
  };

  const formatDuration = (seconds = 0) => {
    const totalSeconds = Math.round(Number(seconds) || 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }

    return `${minutes}m ${secs}s`;
  };

  const formatPace = (secondsPerKm) => {
    if (
      secondsPerKm == null ||
      !Number.isFinite(Number(secondsPerKm)) ||
      Number(secondsPerKm) <= 0
    ) {
      return '—';
    }

    const pace = Math.round(Number(secondsPerKm));

    return `${Math.floor(pace / 60)}:${String(pace % 60).padStart(2, '0')} /km`;
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown date';

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return 'Unknown date';
    }

    return parsedDate.toLocaleString();
  };

  const getDistanceKm = (activity) =>
    (Number(activity.distanceMeters) || 0) / 1000;

  // Calculate totals across all loaded activities.
  const summary = activities.reduce(
    (totals, activity) => {
      totals.distance += Number(activity.distanceMeters) || 0;
      totals.duration += Number(activity.durationSeconds) || 0;
      totals.calories += Number(activity.calories) || 0;
      totals.heartRates += Number(activity.avgHeartRate) || 0;
      totals.heartRateCount +=
        activity.avgHeartRate != null ? 1 : 0;

      return totals;
    },
    {
      distance: 0,
      duration: 0,
      calories: 0,
      heartRates: 0,
      heartRateCount: 0
    }
  );

  const overallPace =
    summary.distance > 0
      ? summary.duration / (summary.distance / 1000)
      : null;

  const averageHeartRate =
    summary.heartRateCount > 0
      ? Math.round(summary.heartRates / summary.heartRateCount)
      : null;

  return (
    <div className="flex flex-column gap-4">
      <Card title="Garmin Activities">
        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            label="Download Garmin Data"
            icon="pi pi-download"
            onClick={handleDownloadGarminData}
          />

          <Button
            label="Show Garmin Files"
            icon="pi pi-folder-open"
            onClick={handleShowGarminData}
            severity="secondary"
          />

          <Button
            label="Show Garmin FIT Files"
            icon="pi pi-folder-open"
            onClick={handleShowGarminDataFIT}
            severity="secondary"
          />

          <Button
            label="Refresh Activities"
            icon="pi pi-refresh"
            onClick={loadActivities}
            severity="secondary"
            outlined
          />
        </div>

        {error && (
          <div className="text-red-500 mb-3" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading activities...</p>
        ) : (
          <>
            {/* Overall summary */}
            <h3>Overall Summary</h3>

            <div className="grid">
              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Total Activities
                  </div>
                  <div className="text-2xl font-bold">
                    {activities.length}
                  </div>
                </Card>
              </div>

              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Total Distance
                  </div>
                  <div className="text-2xl font-bold">
                    {(summary.distance / 1000).toFixed(2)} km
                  </div>
                </Card>
              </div>

              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Total Duration
                  </div>
                  <div className="text-2xl font-bold">
                    {formatDuration(summary.duration)}
                  </div>
                </Card>
              </div>

              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Total Calories
                  </div>
                  <div className="text-2xl font-bold">
                    {summary.calories.toLocaleString()}
                    {' kcal'}
                  </div>
                </Card>
              </div>

              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Overall Average Pace
                  </div>
                  <div className="text-2xl font-bold">
                    {formatPace(overallPace)}
                  </div>
                </Card>
              </div>

              <div className="col-12 md:col-4">
                <Card className="h-full">
                  <div className="text-500 mb-2">
                    Average Heart Rate
                  </div>
                  <div className="text-2xl font-bold">
                    {averageHeartRate != null
                      ? `${averageHeartRate} bpm`
                      : '—'}
                  </div>
                </Card>
              </div>
            </div>

            {/* Individual activities */}
            <h3 className="mt-4">
              All Activities ({activities.length})
            </h3>

            {activities.length === 0 ? (
              <p>No activities found.</p>
            ) : (
              <div className="flex flex-column gap-3">
                {activities
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.startTime) -
                      new Date(a.startTime)
                  )
                  .map((activity, index) => {
                    const id =
                      activity._id ??
                      activity.externalId ??
                      activity.sourceFile ??
                      index;

                    const laps = Array.isArray(activity.laps)
                      ? activity.laps
                      : [];

                    return (
                      <Card
                        key={id}
                        title={`${activity.sport || 'Activity'} — ${formatDate(activity.startTime)}`}
                        subTitle={activity.source || 'Unknown source'}
                      >
                        <div className="grid">
                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Distance
                            </div>
                            <strong>
                              {getDistanceKm(activity).toFixed(2)} km
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Duration
                            </div>
                            <strong>
                              {formatDuration(activity.durationSeconds)}
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Average Pace
                            </div>
                            <strong>
                              {formatPace(activity.avgPaceSecPerKm)}
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Calories
                            </div>
                            <strong>
                              {activity.calories ?? '—'} kcal
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Average Heart Rate
                            </div>
                            <strong>
                              {activity.avgHeartRate != null
                                ? `${activity.avgHeartRate} bpm`
                                : '—'}
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Maximum Heart Rate
                            </div>
                            <strong>
                              {activity.maxHeartRate != null
                                ? `${activity.maxHeartRate} bpm`
                                : '—'}
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Elevation Gain
                            </div>
                            <strong>
                              {activity.elevationGainMeters != null
                                ? `${Number(activity.elevationGainMeters).toFixed(1)} m`
                                : '—'}
                            </strong>
                          </div>

                          <div className="col-6 md:col-3">
                            <div className="text-500">
                              Laps
                            </div>
                            <strong>{laps.length}</strong>
                          </div>
                        </div>

                        {activity.sourceFile && (
                          <p className="text-500 text-sm">
                            File: {activity.sourceFile}
                          </p>
                        )}

                        <Button
                          label={
                            expandedActivities[id]
                              ? 'Hide Laps'
                              : `Show Laps (${laps.length})`
                          }
                          icon={
                            expandedActivities[id]
                              ? 'pi pi-chevron-up'
                              : 'pi pi-chevron-down'
                          }
                          severity="secondary"
                          outlined
                          onClick={() => toggleLaps(id)}
                          disabled={laps.length === 0}
                        />

                        {expandedActivities[id] && (
                          <div className="mt-3">
                            <h4>Lap Details</h4>

                            <div className="overflow-x-auto">
                              <table className="w-full text-left">
                                <thead>
                                  <tr>
                                    <th className="p-2">Lap</th>
                                    <th className="p-2">Distance</th>
                                    <th className="p-2">Duration</th>
                                    <th className="p-2">Pace</th>
                                    <th className="p-2">Avg HR</th>
                                    <th className="p-2">Max HR</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {laps.map((lap, lapIndex) => {
                                    const lapDistanceKm =
                                      (Number(lap.distanceMeters) || 0) /
                                      1000;

                                    const lapPace =
                                      lapDistanceKm > 0
                                        ? (Number(lap.durationSeconds) || 0) /
                                          lapDistanceKm
                                        : null;

                                    return (
                                      <tr key={lapIndex}>
                                        <td className="p-2">
                                          {lapIndex + 1}
                                        </td>
                                        <td className="p-2">
                                          {lapDistanceKm.toFixed(2)} km
                                        </td>
                                        <td className="p-2">
                                          {formatDuration(lap.durationSeconds)}
                                        </td>
                                        <td className="p-2">
                                          {formatPace(lapPace)}
                                        </td>
                                        <td className="p-2">
                                          {lap.avgHeartRate ?? '—'}
                                        </td>
                                        <td className="p-2">
                                          {lap.maxHeartRate ?? '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Optional list of Garmin workout files */}
      {testData?.workouts?.length > 0 && (
        <Card title="Garmin Workout Files">
          <ul>
            {testData.workouts.map((fileName) => (
              <li key={fileName} className="mb-2">
                <Button
                  label={fileName}
                  icon="pi pi-file"
                  link
                  onClick={() => handleShowGarminActivity(fileName)}
                />
              </li>
            ))}
          </ul>

          {selectedGarminActivity != null && (
            <div className="mt-3">
              <h4>Selected Garmin Activity</h4>
              <pre className="overflow-x-auto white-space-pre-wrap">
                {JSON.stringify(selectedGarminActivity, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default TestWidget;
